import React from 'react';
import { CircularProgress, Tooltip } from '@material-ui/core';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';

export default function PcapView(props) {
  const navigate = useNavigate();
  const {
    showOpenPcapButton,
    demoPcapLoading,
    onSelectFile,
    openPcapRef,
    serverName,
    parseAndLoadPcapCsvData,
    pcapBaseGraphRef,
    setShowOpenPcapButton,
    setDemoPcapLoading,
    setIsDataReady,
    setGraphData,
    setIsLoading,
    setProgressVisible,
    setProgress,
    onEngineStop,
    setSnackbarOpen,
    setSnackbarMessage,
    setSnackbarSeverity,
  } = props || {};

  const handleFileChangePCAP = async (event) => {
    const file = event && event.target && event.target.files ? event.target.files[0] : null;
    if (!file) return;

    if (typeof setShowOpenPcapButton === 'function') setShowOpenPcapButton(false);
    let csvData;

    try {
      if (typeof setIsLoading === 'function') setIsLoading(true);
      if (typeof setProgressVisible === 'function') setProgressVisible(true);

      const base = serverName ? String(serverName) : '';
      const apiCall = 'openPCAP';
      const formData = new FormData();
      formData.append('file', file);

      if (file.name.endsWith('.pcap')) {
        const response = await axios.post(`${base}/${apiCall}`, formData, {
          withCredentials: false,
          responseType: 'text',
          transformResponse: [(d) => d],
          timeout: 300000,
          onUploadProgress: function (progressEvent) {
            const loaded = progressEvent && progressEvent.loaded ? progressEvent.loaded : 0;
            const total = progressEvent && progressEvent.total ? progressEvent.total : 0;
            const percentage = total ? Math.round((loaded * 100) / total) : 0;
            if (typeof setProgress === 'function') setProgress(percentage);
            console.log(`Upload progress: ${percentage}%`);
          }
        });

        console.log('Upload complete');
        try {
          const hdrs = response && response.headers ? response.headers : {};
          const maxRowsHdr = hdrs['x-pcap-maxrows'] || hdrs['X-PCAP-MaxRows'];
          const minBytesHdr = hdrs['x-pcap-minbytes'] || hdrs['X-PCAP-MinBytes'];
          const minPacketsHdr = hdrs['x-pcap-minpackets'] || hdrs['X-PCAP-MinPackets'];
          const aggregateHdr = hdrs['x-pcap-aggregate'] || hdrs['X-PCAP-Aggregate'];
          if (maxRowsHdr) {
            const maxRows = parseInt(String(maxRowsHdr), 10);
            const minBytes = parseInt(String(minBytesHdr || ''), 10);
            const minPackets = parseInt(String(minPacketsHdr || ''), 10);
            const agg = String(aggregateHdr || 'ip_port');
            if (typeof setSnackbarSeverity === 'function') setSnackbarSeverity('warning');
            if (typeof setSnackbarMessage === 'function') {
              setSnackbarMessage(`PCAP output may be capped (maxRows=${Number.isFinite(maxRows) ? maxRows : maxRowsHdr}, minBytes=${Number.isFinite(minBytes) ? minBytes : (minBytesHdr || 'n/a')}, minPackets=${Number.isFinite(minPackets) ? minPackets : (minPacketsHdr || 'n/a')}, aggregate=${agg}).`);
            }
            if (typeof setSnackbarOpen === 'function') setSnackbarOpen(true);
          }
        } catch (e) {
        }

        csvData = response.data;
        if (typeof parseAndLoadPcapCsvData === 'function') {
          await parseAndLoadPcapCsvData(csvData);
        }

        try {
          await axios.post(`${base}/api/recentFiles`, { path: file.name }, { withCredentials: true });
        } catch (e) {
        }
      }

      if (file.name.endsWith('.csv')) {
        const reader = new FileReader();
        reader.onload = async (e) => {
          try {
            csvData = e && e.target ? e.target.result : '';
            if (typeof parseAndLoadPcapCsvData === 'function') {
              await parseAndLoadPcapCsvData(csvData);
            }
            try {
              await axios.post(`${base}/api/recentFiles`, { path: file.name }, { withCredentials: true });
            } catch (e2) {
            }
          } catch (error) {
            console.error('Error parsing CSV:', error);
          }
        };
        reader.readAsText(file);
      }
    } catch (error) {
      console.error('Error uploading file:', error);
    } finally {
      try {
        if (typeof setProgressVisible === 'function') setProgressVisible(false);
        if (typeof setIsLoading === 'function') setIsLoading(false);
        if (typeof setIsDataReady === 'function') setIsDataReady(true);
        if (typeof onEngineStop === 'function') onEngineStop();
      } catch (e) {
      }
    }
  };

  const handleLoadDemoPcap = async () => {
    try {
      if (typeof setDemoPcapLoading === 'function') setDemoPcapLoading(true);
      if (typeof setIsDataReady === 'function') setIsDataReady(false);
      if (typeof setGraphData === 'function') setGraphData({ nodes: [], links: [] });

      const base = serverName ? String(serverName) : '';
      console.log('[PCAP][demo] Fetching /e3d_demo.csv');
      const tryFetchCsvText = async (url) => {
        const resp = await fetch(url);
        if (!resp || !resp.ok) {
          return { ok: false, status: resp ? resp.status : null, text: '' };
        }
        const text = await resp.text();
        const head = String(text || '').slice(0, 200).toLowerCase();
        const isHtml = head.includes('<!doctype html') || head.includes('<html');
        return { ok: !isHtml, status: resp.status, text, isHtml };
      };

      const debugPortsOn = (typeof window !== 'undefined' && window.__DEBUG_PCAP_PORTS__);

      let fetched = debugPortsOn ? { ok: false, status: null, text: '', isHtml: false } : await tryFetchCsvText('/e3d_demo.csv');
      if (!fetched.ok) {
        if (fetched.isHtml) {
          console.warn('[PCAP][demo] /e3d_demo.csv returned HTML (likely index.html). Falling back.');
        } else {
          console.warn('[PCAP][demo] /e3d_demo.csv fetch failed. status=', fetched.status);
        }
        const debugPorts = debugPortsOn ? '&debugPorts=1' : '';
        const proxyUrl = `${base}/api/fetchFile?name=${encodeURIComponent('e3d_demo.csv')}${debugPorts}`;
        console.log('[PCAP][demo] Fetching via proxy', proxyUrl);
        fetched = await tryFetchCsvText(proxyUrl);
      }
      if (!fetched.ok) {
        throw new Error('Failed to fetch demo CSV');
      }

      if (typeof parseAndLoadPcapCsvData === 'function') {
        await parseAndLoadPcapCsvData(fetched.text);
      }

      const baseGraph = pcapBaseGraphRef && pcapBaseGraphRef.current ? pcapBaseGraphRef.current : null;
      const nodeCount = baseGraph && baseGraph.nodes ? baseGraph.nodes.length : 0;
      const linkCount = baseGraph && baseGraph.links ? baseGraph.links.length : 0;
      console.log('[PCAP][demo] load complete: nodes=', nodeCount, 'links=', linkCount);

      if (nodeCount > 0) {
        if (typeof setShowOpenPcapButton === 'function') setShowOpenPcapButton(false);
      } else {
        console.warn('[PCAP][demo] Demo CSV produced an empty graph; keeping overlay visible.');
      }
    } catch (error) {
      console.error('Error loading demo PCAP CSV:', error);
    } finally {
      try {
        if (typeof setIsDataReady === 'function') setIsDataReady(true);
        if (typeof onEngineStop === 'function') onEngineStop();
      } catch (e) {
      }
      try {
        if (typeof setDemoPcapLoading === 'function') setDemoPcapLoading(false);
      } catch (e) {
      }
    }
  };

  const handleDownloadPcap2CsvScript = async () => {
    try {
      const base = serverName ? String(serverName) : '';
      const url = `${base}/api/fetchFile?name=${encodeURIComponent('pcap2csv.sh')}`;
      const resp = await fetch(url, { credentials: 'include' });
      if (!resp || !resp.ok) throw new Error('fetch failed');
      const blob = await resp.blob();
      if (!blob) return;
      const dlUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = dlUrl;
      a.download = 'pcap2csv.sh';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(dlUrl);
    } catch (e) {
      try {
        if (typeof setSnackbarSeverity === 'function') setSnackbarSeverity('error');
        if (typeof setSnackbarMessage === 'function') setSnackbarMessage('Failed to download pcap2csv.sh');
        if (typeof setSnackbarOpen === 'function') setSnackbarOpen(true);
      } catch (e2) {
      }
    }
  };

  return (
    <>
      <input
        type="file"
        ref={openPcapRef}
        onChange={(event) => {
          console.log('File input changed');
          handleFileChangePCAP(event);
        }}
        accept=".pcap, .csv"
        style={{ display: 'none' }}
      />

      {showOpenPcapButton && (
        <div
          style={{
            position: 'fixed',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '10px',
            zIndex: 1000,
          }}
        >
      <button
        onClick={onSelectFile}
        style={{
          width: '320px',
          height: '56px',
          padding: '12px 26px',
          fontSize: '18px',
          fontWeight: 'bold',
          backgroundColor: '#007bff',
          color: 'white',
          border: 'none',
          borderRadius: '8px',
          cursor: 'pointer',
          boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.2)',
          whiteSpace: 'nowrap',
        }}
      >
        Select File
      </button>
      <button
        onClick={handleLoadDemoPcap}
        disabled={!!demoPcapLoading}
        style={{
          width: '320px',
          height: '56px',
          padding: '12px 26px',
          fontSize: '18px',
          fontWeight: 'bold',
          backgroundColor: '#28a745',
          color: 'white',
          border: 'none',
          borderRadius: '8px',
          cursor: 'pointer',
          boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.2)',
          opacity: demoPcapLoading ? 0.8 : 1,
          whiteSpace: 'nowrap',
        }}
      >
        {demoPcapLoading ? (
          <span style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <CircularProgress size={16} style={{ color: 'white' }} />
            Loading Demo...
          </span>
        ) : (
          'Load Demo'
        )}
      </button>
      <button
        onClick={() => {
          try {
            navigate('/gallery');
          } catch (e) {
            try { window.location.href = '/gallery'; } catch (e2) {}
          }
        }}
        style={{
          width: '320px',
          height: '56px',
          padding: '12px 26px',
          fontSize: '18px',
          fontWeight: 'bold',
          backgroundColor: '#6c757d',
          color: 'white',
          border: 'none',
          borderRadius: '8px',
          cursor: 'pointer',
          boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.2)',
          whiteSpace: 'nowrap',
        }}
      >
        View Gallery
      </button>
      <button
        onClick={() => {
          try {
            navigate('/pcap/uploads');
          } catch (e) {
            try { window.location.href = '/pcap/uploads'; } catch (e2) {}
          }
        }}
        style={{
          width: '320px',
          height: '56px',
          padding: '12px 26px',
          fontSize: '18px',
          fontWeight: 'bold',
          backgroundColor: '#8e44ad',
          color: 'white',
          border: 'none',
          borderRadius: '8px',
          cursor: 'pointer',
          boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.2)',
          whiteSpace: 'nowrap',
        }}
      >
        Analysis Uploads
      </button>
      <Tooltip title="tshark shell script to extract csv data from pcap" placement="bottom">
        <button
          onClick={handleDownloadPcap2CsvScript}
          style={{
            width: '320px',
            height: '56px',
            padding: '12px 26px',
            fontSize: '18px',
            fontWeight: 'bold',
            backgroundColor: '#343a40',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            cursor: 'pointer',
            boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.2)',
            whiteSpace: 'nowrap',
          }}
        >
          Download pcap2csv.sh
        </button>
      </Tooltip>
        </div>
      )}
    </>
  );
}
